import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckBadgeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import * as api from '../../api/account'
import { useAuth } from '../../context/AuthContext'
import { TEKST_FIRMA_MA_KONTO } from '../../config/rejestracjaFirmy'
import { Button, ErrorText, Field, SectionTitle, inputClass } from './ui'

const PUSTA = { nip: '', name: '', street: '', postal_code: '', city: '', regon: '' }

const CompanySection = ({ profile, setProfile, setFlash }) => {
  const firma = profile.company
  const [form, setForm] = useState(firma ? { ...PUSTA, ...firma } : PUSTA)
  const [busy, setBusy] = useState(false)
  const [szukam, setSzukam] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  // Czy dane pochodzą z rejestru, czy zostały wpisane ręcznie. Decyduje o tym,
  // czy zapiszemy firmę jako zweryfikowaną.
  const [zRejestru, setZRejestru] = useState(Boolean(firma?.verified))
  const [snapshot, setSnapshot] = useState(null)
  // Droga firmy (2026-10-24): po podpięciu nowej firmy pasek i pulpit muszą od razu wiedzieć, że konto jest firmowe
  // (`odswiezProfil`), a następny krok to ankieta na Rynku. Wcześniej pasek dalej pokazywał „Dla firm PV”.
  const { odswiezProfil } = useAuth()
  const [podpieta, setPodpieta] = useState(false)
  const [maKonto, setMaKonto] = useState(false)

  // Konta zespołowe (2026-10-11): dane firmy zmienia WYŁĄCZNIE konto główne; pozostali widzą je bez edycji.
  // Pracownik „odpina firmę”, czyli odchodzi z zespołu.
  const wlasciciel = !firma || firma.role === 'owner'
  const mozeEdytowac = wlasciciel
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const szukajPoNip = async () => {
    setError(''); setInfo(''); setSzukam(true)
    const res = await api.lookupNip(form.nip)
    setSzukam(false)
    if (!res.ok) { setError(res.error); return }

    const d = res.data
    // Cudza firma z kontem: zapis i tak odmówi, więc mówimy to od razu (nie dotyczy poprawek własnej firmy).
    setMaKonto(Boolean(d.ma_konto) && !firma)
    if (d.status === 'found') {
      setForm((f) => ({
        ...f, nip: d.nip, name: d.name || '', street: d.street || '',
        postal_code: d.postal_code || '', city: d.city || '', regon: d.regon || '',
      }))
      setZRejestru(true)
      setSnapshot(d.snapshot || null)
      setInfo(`Znaleziono w rejestrze${d.vat_status ? ` (status VAT: ${d.vat_status})` : ''}.`)
      return
    }
    // Nie znaleziono albo rejestr nie odpowiada — użytkownik wpisuje ręcznie.
    // To NIE jest błąd blokujący: awaria rejestru nie może kosztować nas
    // użytkownika (PLAN_KONTA §5).
    setZRejestru(false)
    setSnapshot(null)
    setInfo(d.message || '')
  }

  const zapisz = async (e) => {
    e.preventDefault()
    setError('')
    setBusy(true)
    const res = await api.saveCompany({ ...form, verified: zRejestru, snapshot })
    setBusy(false)
    if (!res.ok) { setError(res.error); return }
    setProfile(res.data)
    setFlash('Dane firmy zapisane.')
    if (!firma) setPodpieta(true)
    odswiezProfil?.()
  }

  const odepnij = async () => {
    setBusy(true)
    const res = await api.leaveCompany()
    setBusy(false)
    if (!res.ok) { setError(res.error); return }
    setProfile(res.data)
    setForm(PUSTA)
    setZRejestru(false)
    setPodpieta(false)
    odswiezProfil?.()
    setFlash(wlasciciel ? 'Konto nie jest już powiązane z firmą.' : 'Nie jesteś już w zespole firmy.')
  }

  return (
    <form onSubmit={zapisz}>
      <SectionTitle hint={mozeEdytowac
        ? 'Podaj NIP, a resztę danych pobierzemy z Białej listy podatników VAT. Możesz je poprawić przed zapisem.'
        : 'Należysz do zespołu tej firmy. Dane firmy zmienia konto główne.'}>
        Firma
      </SectionTitle>

      {firma?.verified && (
        <div className="mb-4 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50
                        border border-emerald-100 rounded-lg p-2.5">
          <CheckBadgeIcon className="w-5 h-5 flex-shrink-0" />
          Dane potwierdzone w rejestrze państwowym
        </div>
      )}

      <fieldset disabled={!mozeEdytowac} className="m-0 min-w-0 border-0 p-0">
      <div className="flex gap-2 items-end mb-5" data-samouczek="firma-nip">
        <div className="flex-1">
          <Field label="NIP" hint="10 cyfr. Sprawdzamy poprawność, zanim odpytamy rejestr.">
            <input type="text" className={inputClass} value={form.nip} disabled={busy}
                   placeholder="np. 5252248481" onChange={set('nip')} />
          </Field>
        </div>
        {/* Czarny przycisk na ciemnej karcie czytałby się jak wyłączony — w ciemnym
            motywie idzie o dwa stopnie w górę skali, żeby dalej wyglądał na klikalny. */}
        <button
          type="button"
          onClick={szukajPoNip}
          disabled={szukam || busy || !form.nip.trim()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-sm
                     font-semibold hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors shadow-sm mb-[2px]
                     disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <MagnifyingGlassIcon className="w-4 h-4" />
          {szukam ? 'Szukam…' : 'Pobierz dane'}
        </button>
      </div>

      {info && <p className="mb-4 text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg p-2.5">{info}</p>}
      {maKonto && <p className="mb-4 text-sm font-semibold text-amber-800">{TEKST_FIRMA_MA_KONTO}</p>}

      <div className="grid gap-4 mb-4">
        <Field label="Nazwa firmy">
          <input type="text" className={inputClass} value={form.name} disabled={busy} onChange={set('name')} />
        </Field>
        <Field label="Ulica i numer">
          <input type="text" className={inputClass} value={form.street} disabled={busy} onChange={set('street')} />
        </Field>
        <div className="grid sm:grid-cols-[140px_1fr] gap-4">
          <Field label="Kod pocztowy">
            <input type="text" className={inputClass} value={form.postal_code} disabled={busy} onChange={set('postal_code')} />
          </Field>
          <Field label="Miejscowość">
            <input type="text" className={inputClass} value={form.city} disabled={busy} onChange={set('city')} />
          </Field>
        </div>
        <Field label="REGON (opcjonalnie)">
          <input type="text" className={inputClass} value={form.regon} disabled={busy} onChange={set('regon')} />
        </Field>
      </div>
      </fieldset>

      <ErrorText>{error}</ErrorText>

      <div className="mt-5 flex gap-2 flex-wrap">
        {mozeEdytowac && <Button type="submit" busy={busy}>{firma ? 'Zapisz zmiany' : 'Powiąż konto z firmą'}</Button>}
        {firma && (
          <Button variant="ghost" onClick={odepnij} disabled={busy}>{wlasciciel ? 'Odepnij firmę' : 'Opuść zespół'}</Button>
        )}
      </div>

      {podpieta && firma && (
        <p className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-gray-800">
          Firma podpięta. Teraz krótka ankieta, żeby dostawać zlecenia z Rynku:{' '}
          <Link to="/firma/ankieta" className="font-semibold text-blue-700">wypełnij ankietę firmy</Link> (2 minuty).
        </p>
      )}

      {firma && (
        <p className="mt-3 text-xs text-gray-400">
          {wlasciciel
            ? 'Odpięcie nie kasuje firmy ani jej klientów. Przy zespole najpierw usuń pozostałe osoby w zakładce Zespół.'
            : 'Twoi klienci zostaną w firmie i przejdą na konto główne. Do zespołu wrócisz tylko przez nowe zaproszenie.'}
        </p>
      )}
    </form>
  )
}

export default CompanySection
